import { useCallback, useMemo, useState } from 'react';
import { Grid, Tooltip } from '@mui/material';
import { BodyParts, PartsNames } from '../Fields/HumanBodyMap/bodyParts';
import AutoSizer from 'react-virtualized-auto-sizer';
import { amber, grey, red } from '@mui/material/colors';
import { isArray, isEmpty, minBy, maxBy, isFunction } from 'lodash';

// eslint-disable-next-line
const BodyContainer = ({ children, width, height }) => (
  <Grid container style={{ width, height }}>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 375.42 832.97" width="100%" height="auto">
      <g>{children}</g>
    </svg>
  </Grid>
);

// eslint-disable-next-line
const BodyPart = ({ id, tooltip, d, fill, clickable, onMouseEnter, onMouseLeave, onClick }) => {
  const handleMouseEnter = () => {
    onMouseEnter(id);
  };

  const handleMouseLeave = () => {
    onMouseLeave(id);
  };

  return (
    <Tooltip title={tooltip}>
      <path
        d={d}
        id={id}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
        style={{
          WebkitTapHighlightColor: 'transparent',
          fill,
          cursor: clickable ? 'pointer' : '',
        }}
      />
    </Tooltip>
  );
};

export const HumanMapChart = ({ data, onPartClick }) => {
  const [hovered, setHovered] = useState(null);

  // min and max values in kpi data - to determine the part color
  const [minValue, maxValue] = useMemo(() => {
    if (!isArray(data) || isEmpty(data)) return [0, 0];

    const minItem = minBy(data, 'value');
    const maxItem = maxBy(data, 'value');

    return [minItem.value, maxItem.value];
  }, [data]);

  const { frontBodyParts, backBodyParts } = useMemo(() => {
    return {
      frontBodyParts: BodyParts.filter(({ side }) => side === 'front'),
      backBodyParts: BodyParts.filter(({ side }) => side === 'back'),
    };
  }, []);

  const getFill = useCallback(
    (dataItem, id) => {
      if (!dataItem) return grey[600];
      const value = dataItem.value;

      // calculate the item color
      const rangeDiff = maxValue - minValue + 1;
      const itemDiff = value - minValue + 1;
      const ratio = itemDiff / rangeDiff;
      const colorStep = 1 / 8;
      const colorColumn = Math.ceil(ratio / colorStep) * 100;

      return hovered === id ? red[colorColumn + 100] : red[colorColumn];
    },
    [hovered, minValue, maxValue],
  );

  const handleMouseEnter = (id) => {
    if ('ontouchstart' in window) return;
    setHovered(id);
  };

  const handleMouseLeave = () => {
    if ('ontouchstart' in window) return;
    setHovered(null);
  };

  const handlePartClick = (label) => {
    if (!isFunction(onPartClick)) return;

    // there is no part in the kpi data
    if (!data.some((x) => x.label === label)) return;

    onPartClick({ label });
  };

  return (
    <Grid container sx={{ height: '100%' }}>
      <AutoSizer>
        {({ width, height }) => (
          <Grid container spacing={1} justifyContent={'space-between'} alignItems={'flex-start'} sx={{ height, width }}>
            <Grid
              container
              item
              justifyContent={'center'}
              alignItems={'center'}
              lg={6}
              md={6}
              xs={12}
              sx={{ height, py: 2 }}
            >
              <BodyContainer width={width} height={height * 0.9}>
                {frontBodyParts.map((bodyPart, index) => {
                  const label = PartsNames.en[bodyPart.id];
                  const dataItem = isArray(data) ? data.find((x) => x.label === label) : null;
                  const fill = dataItem ? getFill(dataItem, bodyPart.id) : grey[600];
                  const tooltip = dataItem ? `${label} (${dataItem.value})` : '';

                  return (
                    <BodyPart
                      key={index}
                      id={bodyPart.id}
                      d={bodyPart.d}
                      fill={fill}
                      tooltip={tooltip}
                      clickable={Boolean(dataItem)}
                      onClick={() => dataItem && handlePartClick(label)}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </BodyContainer>
            </Grid>
            <Grid container justifyContent={'center'} alignItems={'center'} item lg={6} md={6} xs={12} sx={{ height }}>
              <BodyContainer width={width} height={height * 0.9}>
                {backBodyParts.map((bodyPart, index) => {
                  const label = PartsNames.en[bodyPart.id];
                  const dataItem = isArray(data) ? data.find((x) => x.label === label) : null;
                  const fill = dataItem ? getFill(dataItem, bodyPart.id) : grey[600];
                  const tooltip = dataItem ? `${label} (${dataItem.value})` : '';

                  return (
                    <BodyPart
                      key={index}
                      id={bodyPart.id}
                      d={bodyPart.d}
                      fill={fill}
                      tooltip={tooltip}
                      clickable={Boolean(dataItem)}
                      onClick={() => dataItem && handlePartClick(label)}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </BodyContainer>
            </Grid>
          </Grid>
        )}
      </AutoSizer>
    </Grid>
  );
};

export default HumanMapChart;
